import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { v4 as uuidv4 } from "uuid";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { useUpdateListPersonSendersMutation } from "@/pages/Private/redux/list/list.api";

import { User } from "@/pages/Public/pages/Login/schema/login";

import { CopywritingCampaign } from "@/pages/Private/pages/List/pages/ListEditCopywriting/ListEditCopywriting";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { useCloseOnOutsideClick } from "@/utils/useCloseOnOutsideClick";

import { Sender } from "../../Sender";
import { resolveSenderInfoTooltipPosition } from "./senderInfoTooltipPosition.util";
import { SendersInfo } from "./SendersInfo";

export const SenderInfo: FunctionComponent<{
	row: ListPerson;
	selectedCampaignConfig?: CopywritingCampaign | null;
	direction?: string;
	className?: string;
}> = ({ row, selectedCampaignConfig, direction = "bottom", className }) => {
	const [isVisible, setIsVisible] = useState<boolean>(false);

	const { data: sendingUsers } = useGetSendersUsersQuery();

	const defaultSenders = useMemo<Sender[]>(() => {
		return (
			sendingUsers?.data?.reduce((acc: Sender[], user: User) => {
				if (user.unipile?.isLoggedIn) {
					acc.push({
						userId: user.id,
						active: true,
						name: user.firstName + " " + user.lastName,
						type: "linkedin",
						emailAddress: user.email,
						firstName: user.firstName,
						lastName: user.lastName,
						emailType: "primary",
						uuid: uuidv4(),
						deactivated: false,
					});
				}

				if (user.emails?.length && user.emails.length > 0) {
					user.emails.forEach((email) => {
						acc.push({
							userId: user.id,
							active: true,
							name: user.firstName + " " + user.lastName,
							type: "email",
							firstName: user.firstName,
							lastName: user.lastName,
							emailId: email.id || undefined,
							emailAddress: email.email,
							emailType: email.type,
							uuid: uuidv4(),
							deactivated: false,
						});
					});
				}

				return acc;
			}, []) || []
		);
	}, [sendingUsers]);

	const currentSenders = useMemo<Sender[]>(() => {
		// Layer 3: prospect-level sender overrides take priority over campaign-level senders
		const prospectSenders = row.senders;

		if (prospectSenders && prospectSenders.length > 0) {
			return prospectSenders;
		}

		return selectedCampaignConfig?.senders || defaultSenders;
	}, [row.senders, selectedCampaignConfig, defaultSenders]);

	const [senders, setSenders] = useState<Sender[]>(currentSenders);

	useEffect(() => {
		setSenders(currentSenders);
	}, [currentSenders]);

	const activeSenders = useMemo(() => {
		return currentSenders.filter((sender: Sender) => sender.active);
	}, [currentSenders]);

	const senderNames = useMemo(() => {
		return activeSenders
			.map((sender: Sender) => sender.name)
			.reduce((acc: string[], name: string) => {
				if (acc.includes(name)) {
					return acc;
				} else {
					return [...acc, name];
				}
			}, []);
	}, [activeSenders]);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	const [updateListPersonSenders, { isLoading: isUpdatingListPerson }] =
		useUpdateListPersonSendersMutation();

	const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		await updateListPersonSenders({
			id: row.id,
			senders: senders,
		}).unwrap();

		setIsVisible(false);
	};

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useEffect(() => {
		if (isVisible && triggerRef.current && tooltipRef.current) {
			const triggerRect = triggerRef.current.getBoundingClientRect();
			const tooltipRect = tooltipRef.current.getBoundingClientRect();
			const { top, left } = resolveSenderInfoTooltipPosition({
				direction,
				trigger: triggerRect,
				tooltip: tooltipRect,
				viewport: { width: window.innerWidth, height: window.innerHeight },
			});

			tooltipRef.current.style.left = `${left}px`;

			tooltipRef.current.style.top = `${top}px`;
		}
	}, [isVisible]);

	const renderChangeResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className={`fixed flex flex-col z-[100000] p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[300px] `}
			>
				<div className="flex flex-col gap-2">
					<SendersInfo
						senders={senders}
						sendingUsers={sendingUsers?.data ?? []}
						setSenders={setSenders}
					/>
				</div>
				<div className="flex flex-row gap-2 justify-end">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={senders.filter((sender: Sender) => sender.active).length === 0}
						title="Close"
						onClick={() => {
							setIsVisible(false);
							setSenders(currentSenders);
						}}
					/>
					<Button
						color={ButtonColor.ACTION}
						disabled={senders.filter((sender: Sender) => sender.active).length === 0}
						isLoading={isUpdatingListPerson}
						title="Save"
						onClick={handleSave}
					/>
				</div>
			</div>,
			document.body
		);
	};

	const renderSenderInfo = (content: string) => {
		return (
			<div
				ref={triggerRef}
				className={`absolute top-0 left-0 bg-blue-action rounded-full w-4 h-4 flex flex-row items-center justify-center cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold`}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();

					if (senders.filter((sender: Sender) => sender.active).length === 0) {
						return;
					}

					setIsVisible?.(true);
				}}
			>
				{content}
			</div>
		);
	};

	return (
		<Tooltip
			content={
				<>
					<p>{senderNames.length > 1 ? "Senders " : "Sender"}:</p>
					{senderNames.map((name) => {
						return (
							<div key={name}>
								<p className="flex gap-2 flex-col">{name} </p>
							</div>
						);
					})}
				</>
			}
		>
			<div className={`flex flex-row gap-2 relative ${className}`}>
				{senderNames && senderNames.length > 1 && senderNames.length === 0
					? renderSenderInfo(activeSenders.length.toString())
					: senderNames && senderNames.length === 1
						? renderSenderInfo(
								`${activeSenders[0].name?.split(" ")[0]?.[0]}${
									activeSenders[0].name?.split(" ")[1]?.[0]
								}`
							)
						: senderNames.length > 1
							? renderSenderInfo(`${senderNames.length}`)
							: renderSenderInfo(`?`)}

				{isVisible && renderChangeResult()}
			</div>
		</Tooltip>
	);
};
